import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import { Plus, Download, FileText, Eye, Search } from 'lucide-react';
import { toast } from 'sonner';
import { notify } from '@/lib/notifications/engine';

export default function Invoices() {
  const [invoices, setInvoices] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [detailDialog, setDetailDialog] = useState(null);
  const [generateConfirm, setGenerateConfirm] = useState(false);
  const [form, setForm] = useState({ participant_id: '', period_start: '', period_end: '' });
  const [loading, setLoading] = useState(false);

  const fetchData = async () => {
    try {
      const [invRes, partRes] = await Promise.all([api.get('/invoices'), api.get('/participants')]);
      setInvoices(invRes.data.data || []); setParticipants(partRes.data.data || []);
    } catch (e) { notify.error(e, 'INVOICE_LOAD_FAILED'); }
  };
  useEffect(() => { fetchData(); }, []);

  const handleGenerate = async () => {
    setLoading(true);
    try {
      const res = await api.post('/invoices/generate', form);
      const inv = res?.data?.data || {};
      const splitSummary = inv.auto_split_summary;
      if (inv.is_split_billed && splitSummary?.lines > 1) {
        toast.success(
          `Invoice ${inv.invoice_number} generated · Split-billed (auto) · ${splitSummary.lines} lines across ${splitSummary.shifts} shift${splitSummary.shifts > 1 ? 's' : ''}`,
          { duration: 6000 }
        );
      } else {
        toast.success(`Invoice ${inv.invoice_number || ''} generated`.trim());
      }
      setGenerateConfirm(false); setDialogOpen(false);
      setForm({ participant_id: '', period_start: '', period_end: '' }); fetchData();
    } catch (e) { notify.error(e, 'INVOICE_GENERATE_FAILED'); }
    finally { setLoading(false); }
  };

  const exportCSV = async (id) => {
    try {
      const res = await api.get(`/invoices/${id}/export-csv`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data])); const a = document.createElement('a');
      a.href = url; a.download = `invoice-${id}.csv`; a.click(); toast.success('CSV downloaded');
    } catch (e) { notify.error(e, 'INVOICE_CSV_EXPORT_FAILED'); }
  };

  const exportPDF = async (id) => {
    try {
      const res = await api.get(`/invoices/${id}/export-pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' })); const a = document.createElement('a');
      a.href = url; a.download = `invoice-${id}.pdf`; a.click(); toast.success('PDF downloaded');
    } catch (e) { notify.error(e, 'INVOICE_PDF_EXPORT_FAILED'); }
  };

  const viewDetail = async (inv) => {
    try {
      const res = await api.get(`/invoices/${inv.id}`);
      const detail = res.data.data;
      // Phase 3F.5: if split-billed, also fetch the per-allocation breakdown.
      let allocationLines = null;
      if (detail?.is_split_billed) {
        try {
          const alloc = await api.get(`/invoices/${inv.id}/allocation-lines`);
          allocationLines = alloc.data?.data;
        } catch {
          allocationLines = null;
        }
      }
      setDetailDialog({ ...detail, _allocation_lines: allocationLines });
    }
    catch (e) { notify.error(e, 'INVOICE_DETAIL_LOAD_FAILED'); }
  };

  const filtered = invoices.filter(inv => {
    if (!search) return true;
    return inv.invoice_number?.toLowerCase().includes(search.toLowerCase()) ||
           inv.participant_name?.toLowerCase().includes(search.toLowerCase());
  });

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Invoices</h1>
          <p className="text-muted-foreground mt-1">Generate and export NDIS invoices</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="invoice-generate-button"><Plus className="h-4 w-4 mr-2" /> Generate Invoice</Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader><DialogTitle>Generate Invoice</DialogTitle><DialogDescription>Generate from approved, un-invoiced timesheets</DialogDescription></DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Participant</Label>
                <Select value={form.participant_id} onValueChange={(v) => setForm({...form, participant_id: v})}>
                  <SelectTrigger data-testid="invoice-participant-select"><SelectValue placeholder="Select participant" /></SelectTrigger>
                  <SelectContent>{participants.map(p => <SelectItem key={p.id} value={p.id}>{p.first_name} {p.last_name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><Label>Period Start</Label><Input type="date" value={form.period_start} onChange={(e) => setForm({...form, period_start: e.target.value})} required /></div>
                <div className="space-y-2"><Label>Period End</Label><Input type="date" value={form.period_end} onChange={(e) => setForm({...form, period_end: e.target.value})} required /></div>
              </div>
              <Button className="w-full" onClick={() => setGenerateConfirm(true)} disabled={!form.participant_id || !form.period_start || !form.period_end} data-testid="invoice-submit-button">
                Review & Generate
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1 md:w-[320px] md:flex-none">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Search by invoice # or participant..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="invoice-search" />
        </div>
        <p className="text-sm text-muted-foreground">{filtered.length} invoice(s)</p>
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Invoice #</TableHead>
                <TableHead>Participant</TableHead>
                <TableHead>Period</TableHead>
                <TableHead className="text-right">Total</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">No invoices</TableCell></TableRow>
              ) : filtered.map(inv => (
                <TableRow key={inv.id} className="hover:bg-muted/60">
                  <TableCell className="font-mono text-xs">
                    <div className="flex items-center gap-2">
                      <span>{inv.invoice_number}</span>
                      {inv.is_split_billed && (
                        <span
                          data-testid={`invoice-split-badge-${inv.id}`}
                          className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-violet-50 text-violet-700 border border-violet-200"
                          title={`Split-billed · ${inv.auto_split_summary?.lines || inv.line_items?.length || ''} allocation lines`}
                        >
                          Split-billed{inv.source_orchestrator === 'phase3f_auto' ? ' (auto)' : ''}
                        </span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="font-medium">{inv.participant_name || 'N/A'}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{inv.period_start} to {inv.period_end}</TableCell>
                  <TableCell className="text-right font-mono tabular-nums font-medium">${(inv.total_amount || 0).toFixed(2)}</TableCell>
                  <TableCell><StatusBadge status={inv.status || 'GENERATED'} /></TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Button variant="ghost" size="sm" onClick={() => viewDetail(inv)} data-testid={`invoice-view-${inv.id}`}><Eye className="h-3 w-3" /></Button>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" data-testid={`invoice-export-menu-${inv.id}`}><Download className="h-3 w-3" /></Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent>
                          <DropdownMenuItem onClick={() => exportCSV(inv.id)} data-testid="invoice-export-csv-item"><FileText className="h-3 w-3 mr-2" /> Export CSV</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => exportPDF(inv.id)} data-testid="invoice-export-pdf-item"><FileText className="h-3 w-3 mr-2" /> Export PDF</DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Generate Confirmation */}
      <ConfirmDialog
        open={generateConfirm}
        onOpenChange={setGenerateConfirm}
        title="Generate Invoice"
        description={`Generate invoice for ${participants.find(p => p.id === form.participant_id)?.first_name || 'participant'} from ${form.period_start} to ${form.period_end}? This will mark included timesheets as INVOICED.`}
        confirmLabel="Generate Invoice"
        loading={loading}
        onConfirm={handleGenerate}
      />

      {/* Invoice Detail Dialog */}
      <Dialog open={!!detailDialog} onOpenChange={() => setDetailDialog(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Invoice {detailDialog?.invoice_number}</DialogTitle><DialogDescription>Invoice details and line items</DialogDescription></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div><span className="text-muted-foreground">Participant:</span> <span className="font-medium">{detailDialog?.participant_name}</span></div>
              <div><span className="text-muted-foreground">NDIS:</span> <span className="font-mono">{detailDialog?.ndis_number}</span></div>
              <div><span className="text-muted-foreground">Period:</span> {detailDialog?.period_start} to {detailDialog?.period_end}</div>
              <div><span className="text-muted-foreground">Total:</span> <span className="font-bold text-lg">${(detailDialog?.total_amount || 0).toFixed(2)}</span></div>
            </div>
            {detailDialog?.is_split_billed && (
              <div
                className="rounded-md border border-violet-200 bg-violet-50 p-3 text-xs text-violet-900"
                data-testid="invoice-split-billed-banner"
              >
                <span className="font-semibold">Split-billed invoice</span>
                {' · '}
                {detailDialog?._allocation_lines?.totals?.line_count ?? (detailDialog?.line_items || []).length} allocation
                {(detailDialog?._allocation_lines?.totals?.line_count ?? (detailDialog?.line_items || []).length) === 1 ? '' : 's'}
                {' · GST $'}
                {(detailDialog?._allocation_lines?.totals?.gst ?? 0).toFixed(2)}
              </div>
            )}
            <Table>
              <TableHeader>
                <TableRow><TableHead>Date</TableHead><TableHead>Service</TableHead><TableHead>Staff</TableHead><TableHead className="text-right">Hours</TableHead><TableHead className="text-right">Rate</TableHead><TableHead className="text-right">Amount</TableHead></TableRow>
              </TableHeader>
              <TableBody>
                {(detailDialog?.line_items || []).map((item, i) => (
                  <TableRow key={i} data-testid={item.allocation_id ? `inv-alloc-line-${item.allocation_id}` : `inv-line-${i}`}>
                    <TableCell className="font-mono text-xs">{item.date}</TableCell>
                    <TableCell>
                      {item.service_name}
                      {item.allocation_id && (
                        <div className="mt-0.5 text-[10px] text-violet-700 font-mono">
                          {item.support_item_number || '—'}
                          {item.funding_plan_id ? ` · ${String(item.funding_plan_id).slice(0, 8)}` : ''}
                          {item.gst_treatment === 'GST_APPLICABLE' ? ` · +$${(item.gst_amount || 0).toFixed(2)} GST` : ''}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>{item.staff_name}</TableCell>
                    <TableCell className="text-right tabular-nums">{item.hours}</TableCell>
                    <TableCell className="text-right tabular-nums">${item.rate?.toFixed(2)}</TableCell>
                    <TableCell className="text-right tabular-nums font-medium">${item.amount?.toFixed(2)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
